/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];

    // find the node with bt capability
    let btNode = '';
    const device = PlatformUtils.getDeviceDefinition(feature.project.device.id);
    for (let i = 0; i < device.peripherals.length; i++) {
        const peripheral = device.peripherals[i];
        if (peripheral.type === 'bluetooth') {
            btNode = peripheral.id;
            break;
        }
    }

    if (btNode === '') {
        return blocks;
    }

    blocks.push({
        location: 'APP_OVERLAY',
        blockType: BlockType.CODE,
        template: `
&${btNode} {
    status = "okay";
};
    `,
    });

    blocks.push({
        location: 'APP_OVERLAY_CHOSEN',
        blockType: BlockType.CODE,
        template: `
zephyr,bt-hci = &${btNode};
`,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        blockType: BlockType.CODE,
        id: '#include <zephyr/sys/byteorder.h>',
        template: `
#include <zephyr/sys/byteorder.h>
`,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        blockType: BlockType.CODE,
        template: `
#include <zephyr/bluetooth/bluetooth.h>
#include <zephyr/bluetooth/hci.h>
#include <zephyr/bluetooth/conn.h>
#include <zephyr/bluetooth/uuid.h>
#include <zephyr/bluetooth/gatt.h>
`,
    });

    blocks.push({
        location: 'PRJ_CONF',
        blockType: BlockType.CODE,
        template: `
CONFIG_BT=y
CONFIG_BT_SMP=y
CONFIG_BT_SIGNING=y
CONFIG_BT_PERIPHERAL=y
CONFIG_BT_DIS=y
CONFIG_BT_ATT_PREPARE_COUNT=5
CONFIG_BT_PRIVACY=y
CONFIG_BT_DEVICE_NAME="${feature.getSettingValue('DEVICENAME')}"
CONFIG_BT_DEVICE_APPEARANCE=833
CONFIG_BT_DEVICE_NAME_DYNAMIC=y
CONFIG_BT_DEVICE_NAME_MAX=65

CONFIG_BT_KEYS_OVERWRITE_OLDEST=y
CONFIG_BT_SETTINGS=y
`,
    });

    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        template: `
/* Custom Service Variables */
#define BT_UUID_CUSTOM_SERVICE_VAL \\
    BT_UUID_128_ENCODE(0x12345678, 0x1234, 0x5678, 0x1234, 0x56789abcdef0)

static const struct bt_uuid_128 vnd_uuid = BT_UUID_INIT_128(
    BT_UUID_CUSTOM_SERVICE_VAL);

`,
    });

    blocks.push({
        location: 'GLOBAL_TYPES',
        blockType: BlockType.CODE,
        template: `
static const struct bt_data ad[] = {
    BT_DATA_BYTES(BT_DATA_FLAGS, (BT_LE_AD_GENERAL | BT_LE_AD_NO_BREDR)),
    // BT_DATA_BYTES(BT_DATA_UUID16_ALL,
    BT_DATA_BYTES(BT_DATA_UUID128_ALL, BT_UUID_CUSTOM_SERVICE_VAL),
};

static const struct bt_data sd[] = {
    BT_DATA(BT_DATA_NAME_COMPLETE, CONFIG_BT_DEVICE_NAME, sizeof(CONFIG_BT_DEVICE_NAME) - 1),
};

`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
static void bt_ready(void)
{
    int err;

    LOG_INF("Bluetooth initialized\\n");

    err = bt_le_adv_start(BT_LE_ADV_CONN, ad, ARRAY_SIZE(ad), sd, ARRAY_SIZE(sd));
    if (err) {
        LOG_ERR("Advertising failed to start (err %d)\\n", err);
        return;
    }

    LOG_INF("Advertising successfully started\\n");
}
`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
int bt_err = bt_enable(NULL);
string bt_name = ${feature.project.parseCodeExpression(feature.getSettingValue('DEVICENAME'), [], TibboDataType.STRING)};
bt_set_name(bt_name.c_str());
if (bt_err) {
    LOG_ERR("Bluetooth init failed");
} else {
    bt_ready();
}
`,
    });

    return blocks;
};
